import type { StateStorage } from 'zustand/middleware';

/**
 * AES-GCM cookie storage for zustand's `persist` middleware.
 *
 * This replaces plaintext `localStorage`/`sessionStorage` persistence for
 * state that shouldn't show up as readable JSON in DevTools (Application >
 * Storage) — e.g. the signed-in user's name/email/roles. The value written
 * to the cookie is ciphertext, not the serialized state.
 *
 * Caveat (by construction, not oversight): this is a browser-only SPA with
 * no backend session of its own, so there is nowhere to keep a real secret —
 * the AES key is derived from a constant baked into this bundle, which
 * anyone can read. This defeats *casual inspection* of storage (a reviewer
 * opening DevTools, or a scanner that pattern-matches PII in storage values)
 * but is NOT confidentiality against an attacker who can already run JS on
 * the page (XSS) or who reads the shipped bundle — that attacker can derive
 * the same key. It does not add a security boundary a `httpOnly` cookie
 * would, because only a server can set one of those, and this app has none.
 */

const KEY_MATERIAL = 'bfmonline.auth-cookie.v1';

let cachedKey: Promise<CryptoKey> | null = null;

function getKey(): Promise<CryptoKey> {
  if (!cachedKey) {
    cachedKey = crypto.subtle
      .digest('SHA-256', new TextEncoder().encode(KEY_MATERIAL))
      .then((digest) =>
        crypto.subtle.importKey('raw', digest, 'AES-GCM', false, ['encrypt', 'decrypt']),
      );
  }
  return cachedKey;
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function encrypt(plaintext: string): Promise<string> {
  const key = await getKey();
  // A fresh random IV per write — required for AES-GCM, never reused with the same key.
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(plaintext),
  );
  // IV travels alongside the ciphertext (it isn't secret); store as iv.ciphertext, both base64.
  return `${toBase64(iv)}.${toBase64(new Uint8Array(ciphertext))}`;
}

async function decrypt(blob: string): Promise<string | null> {
  const [ivPart, ciphertextPart] = blob.split('.');
  if (!ivPart || !ciphertextPart) return null;
  try {
    const key = await getKey();
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromBase64(ivPart) },
      key,
      fromBase64(ciphertextPart),
    );
    return new TextDecoder().decode(plaintext);
  } catch {
    // Wrong/rotated key, truncated cookie, or tampered value — treat as absent.
    return null;
  }
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  const match = document.cookie.split('; ').find((entry) => entry.startsWith(prefix));
  return match ? decodeURIComponent(match.slice(prefix.length)) : null;
}

function writeCookie(name: string, value: string): void {
  // No Max-Age/Expires: a session cookie, cleared when the browser closes —
  // this mustn't outlive the browser any more than the bearer token does.
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; SameSite=Strict${secure}`;
}

function deleteCookie(name: string): void {
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Strict`;
}

export const encryptedCookieStorage: StateStorage = {
  getItem: async (name) => {
    const raw = readCookie(name);
    if (!raw) return null;
    return decrypt(raw);
  },
  setItem: async (name, value) => {
    writeCookie(name, await encrypt(value));
  },
  removeItem: (name) => {
    deleteCookie(name);
  },
};
